// Registro de demos ao vivo do Catálogo de Componentes.
// Mapeamento: numero da peça → função que renderiza a demo.
// NÃO importar aqui nada que faça chamada de API — todos os dados são fixos/canned.
// O Modal da peça 001 tem tratamento especial: a demo abre um Modal aninhado em z-[1100].
import { useState } from 'react';
import { Edit, Trash2, User, Phone, MapPin } from 'lucide-react';

import { Modal } from '../../components/Modal';
import { MenuAcoesLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { SecaoForm } from '../../components/SecaoForm';
import { ComboBox } from '../../components/ComboBox';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { UpperInput } from '../../components/inputs/UpperInput';
import { PhoneInput } from '../../components/inputs/PhoneInput';

// ---------------------------------------------------------------------------
// 001 — Modal padrão
// O overlay do Modal padrão é z-1000. O modal interno precisa z-[1100].
// Solução: aplicar z-index elevado no overlay do modal aninhado via inlineStyle
// injetando estilo global temporário. Na prática, o Modal usa a classe
// .modal-overlay que define z-index:1000. Para o modal de exemplo ficar por cima,
// abrimos ele fora da hierarquia do modal externo (estado no próprio componente demo).
// ---------------------------------------------------------------------------
function Demo001Modal() {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
      <p style={{ fontSize: 13, color: 'var(--page-fg)', margin: 0 }}>
        Clique no botão abaixo para ver um Modal de exemplo abrindo por cima desta janela.
      </p>
      <button
        type="button"
        className="btn-primario"
        onClick={() => setAberto(true)}
      >
        Abrir Modal de exemplo
      </button>
      {/* z-[1100] aplicado inline no wrapper pra sobrepor o modal externo (z-1000) */}
      {aberto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'contents' }}>
          <Modal
            aberto={aberto}
            onFechar={() => setAberto(false)}
            titulo="Exemplo de Modal"
            largura={420}
            rodape={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn-secundario" onClick={() => setAberto(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn-primario" onClick={() => setAberto(false)}>
                  Confirmar
                </button>
              </div>
            }
          >
            <p style={{ margin: 0, fontSize: 14 }}>
              Este modal só fecha pelo botão X, Cancelar, Confirmar ou Esc — nunca por clique no
              fundo. Ele também é arrastável pelo cabeçalho.
            </p>
          </Modal>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 002 — Menu de ações da linha ⋮
// ---------------------------------------------------------------------------
function Demo002MenuAcoes() {
  const [ultimo, setUltimo] = useState<string | null>(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: 'var(--page-fg)' }}>
        Linha de exemplo
      </span>
      <MenuAcoesLinha
        acoes={[
          {
            label: 'Editar',
            icon: Edit,
            onClick: () => setUltimo('Editar clicado'),
          },
          {
            label: 'Ver detalhes',
            icon: User,
            onClick: () => setUltimo('Ver detalhes clicado'),
          },
          {
            label: 'Excluir',
            icon: Trash2,
            onClick: () => setUltimo('Excluir clicado'),
            perigoso: true,
          },
        ]}
      />
      {ultimo && (
        <span style={{ fontSize: 12, background: '#fde68a', color: '#000', padding: '2px 8px', borderRadius: 6 }}>
          {ultimo}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 003 — Selo Ativo / Inativo
// ---------------------------------------------------------------------------
function Demo003StatusBadge() {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        <StatusBadge ativo={true} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ativo=true</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        <StatusBadge ativo={false} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ativo=false</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        <StatusBadge ativo={true} labelAtivo="Publicado" labelInativo="Rascunho" />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>label custom</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        <StatusBadge ativo={false} labelAtivo="Publicado" labelInativo="Rascunho" />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>label custom inativo</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 004 — Bloco de formulário (SecaoForm)
// ---------------------------------------------------------------------------
function Demo004SecaoForm() {
  return (
    <div className="form-grid" style={{ gap: 12 }}>
      <SecaoForm icone={User} titulo="Identificação" primeira />
      <div>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: 'var(--page-fg)' }}>
          Nome
        </label>
        <input type="text" placeholder="Ex.: João da Silva" readOnly />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: 'var(--page-fg)' }}>
          CPF / CNPJ
        </label>
        <input type="text" placeholder="000.000.000-00" readOnly />
      </div>
      <SecaoForm icone={Phone} titulo="Contato" />
      <div>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: 'var(--page-fg)' }}>
          Telefone
        </label>
        <input type="text" placeholder="(64) 99999-9999" readOnly />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: 'var(--page-fg)' }}>
          E-mail
        </label>
        <input type="text" placeholder="email@exemplo.com" readOnly />
      </div>
      <SecaoForm icone={MapPin} titulo="Endereço" />
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: 'var(--page-fg)' }}>
          Rua
        </label>
        <input type="text" placeholder="Rua das Flores, 123" readOnly />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 005 — ComboBox
// ---------------------------------------------------------------------------
function Demo005ComboBox() {
  const [valor, setValor] = useState<string | number>('');
  const opcoes = [
    { value: '1', label: 'Produto A — Cabo 2,5mm' },
    { value: '2', label: 'Produto B — Disjuntor 20A' },
    { value: '3', label: 'Produto C — Tomada 2P+T' },
    { value: '4', label: 'Produto D — Interruptor simples' },
  ];
  return (
    <div style={{ maxWidth: 340 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--page-fg)' }}>
        Selecione um produto:
      </label>
      <ComboBox
        opcoes={opcoes}
        value={valor}
        onChange={(v) => setValor(v)}
        placeholder="Digite para filtrar..."
      />
      {valor ? (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Selecionado: <b>{opcoes.find((o) => String(o.value) === String(valor))?.label}</b>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 006 — MoneyInput
// ---------------------------------------------------------------------------
function Demo006MoneyInput() {
  const [valor, setValor] = useState(0);
  return (
    <div style={{ maxWidth: 220 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--page-fg)' }}>
        Valor (R$):
      </label>
      <MoneyInput valor={valor} onChangeValor={setValor} placeholder="0,00" />
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        Valor numérico: <b>{valor}</b>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 007 — UpperInput
// ---------------------------------------------------------------------------
function Demo007UpperInput() {
  const [texto, setTexto] = useState('');
  return (
    <div style={{ maxWidth: 300 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--page-fg)' }}>
        Nome do cliente (sempre maiúsculas):
      </label>
      <UpperInput
        value={texto}
        onChange={setTexto}
        placeholder="Digite qualquer texto..."
      />
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        Tudo que você digitar vira CAIXA ALTA automaticamente.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 008 — PhoneInput
// ---------------------------------------------------------------------------
function Demo008PhoneInput() {
  const [digitos, setDigitos] = useState('');
  return (
    <div style={{ maxWidth: 260 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--page-fg)' }}>
        Telefone / WhatsApp:
      </label>
      <PhoneInput value={digitos} onChange={setDigitos} placeholder="(64) 99999-9999" />
      {digitos && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Dígitos guardados: <b>{digitos}</b>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registro central: numero → função de render da demo
// ---------------------------------------------------------------------------
// Obs.: a peça 013 "Pix do balcão" (CobrancaPix) NÃO tem demo ao vivo — ela cobra ao
// montar (POST real), e montá-la aqui geraria um QR de verdade no terminal do balcão.
// O cartão dela no Catálogo fica só com a ficha; o estado real se vê no /caixa.
export const CATALOGO_DEMOS: Record<string, () => React.ReactElement> = {
  '001': () => <Demo001Modal />,
  '002': () => <Demo002MenuAcoes />,
  '003': () => <Demo003StatusBadge />,
  '004': () => <Demo004SecaoForm />,
  '005': () => <Demo005ComboBox />,
  '006': () => <Demo006MoneyInput />,
  '007': () => <Demo007UpperInput />,
  '008': () => <Demo008PhoneInput />,
};
